import React, { useEffect, useState } from 'react';
import {
  Plus,
  Pencil,
  X,
  Save,
  RefreshCw,
  AlertTriangle,
  ShieldOff,
  Ban,
  CheckCircle2,
  Gavel,
  Building2,
} from 'lucide-react';
import adminChannelPartnerService, {
  ChannelPartner,
  ChannelPartnerInput,
} from '../../../services/adminChannelPartnerService';
import './ManageChannelPartners.css';

const EMPTY_FORM: ChannelPartnerInput = {
  company_name: '',
  phone_number: '',
  person_name: '',
  email: '',
  company_address: '',
  company_product_service: '',
};

const ManageChannelPartners: React.FC = () => {
  const [partners, setPartners] = useState<ChannelPartner[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [form, setForm] = useState<ChannelPartnerInput>(EMPTY_FORM);
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);

  const [revokeTarget, setRevokeTarget] = useState<ChannelPartner | null>(null);
  const [revoking, setRevoking] = useState(false);

  const [togglingId, setTogglingId] = useState<number | null>(null);

  const [toast, setToast] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const showToast = (type: 'success' | 'error', text: string) => {
    setToast({ type, text });
    window.setTimeout(() => setToast(null), 3500);
  };

  const loadPartners = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await adminChannelPartnerService.getAll();
      setPartners(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load channel partners');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadPartners();
  }, []);

  const openCreateModal = () => {
    setEditingId(null);
    setForm(EMPTY_FORM);
    setFormErrors({});
    setIsModalOpen(true);
  };

  const openEditModal = (partner: ChannelPartner) => {
    setEditingId(partner.id);
    setForm({
      company_name: partner.company_name,
      phone_number: partner.phone_number,
      person_name: partner.person_name,
      email: partner.email || '',
      company_address: partner.company_address || '',
      company_product_service: partner.company_product_service || '',
    });
    setFormErrors({});
    setIsModalOpen(true);
  };

  const closeModal = () => {
    if (saving) return;
    setIsModalOpen(false);
  };

  const validate = (): boolean => {
    const errors: Record<string, string> = {};
    if (!form.company_name.trim()) errors.company_name = 'Company name is required';
    if (!form.person_name.trim()) errors.person_name = 'Contact person name is required';
    if (!editingId) {
      const digits = form.phone_number.replace(/\D/g, '');
      if (digits.length !== 10) errors.phone_number = 'Enter a valid 10-digit phone number';
    }
    if (form.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
      errors.email = 'Invalid email format';
    }
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;

    setSaving(true);
    try {
      if (editingId) {
        const updated = await adminChannelPartnerService.update(editingId, {
          company_name: form.company_name.trim(),
          person_name: form.person_name.trim(),
          email: form.email?.trim() || undefined,
          company_address: form.company_address?.trim() || undefined,
          company_product_service: form.company_product_service?.trim() || undefined,
        });
        setPartners((prev) => prev.map((p) => (p.id === editingId ? updated : p)));
        showToast('success', 'Channel partner updated');
      } else {
        const { partner: created, notifications } = await adminChannelPartnerService.create({
          ...form,
          company_name: form.company_name.trim(),
          person_name: form.person_name.trim(),
          phone_number: form.phone_number.replace(/\D/g, ''),
          email: form.email?.trim() || undefined,
        });
        setPartners((prev) => [created, ...prev]);

        const smsNote =
          notifications.sms === 'sent' ? 'SMS sent' : notifications.sms === 'failed' ? 'SMS failed to send' : null;
        const emailNote =
          notifications.email === 'sent'
            ? 'email sent'
            : notifications.email === 'failed'
            ? 'email failed to send'
            : notifications.email === 'skipped_no_email'
            ? 'no email on file'
            : null;
        const deliveryNote = [smsNote, emailNote].filter(Boolean).join(', ');

        showToast(
          notifications.sms === 'failed' || notifications.email === 'failed' ? 'error' : 'success',
          `"${created.company_name}" can now log in with ${created.phone_number}${
            deliveryNote ? ` (${deliveryNote})` : ''
          }`
        );
      }
      setIsModalOpen(false);
    } catch (err) {
      showToast('error', err instanceof Error ? err.message : 'Failed to save channel partner');
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (partner: ChannelPartner) => {
    setTogglingId(partner.id);
    try {
      const updated = await adminChannelPartnerService.setActive(partner.id, !partner.is_active);
      setPartners((prev) => prev.map((p) => (p.id === partner.id ? updated : p)));
      showToast('success', updated.is_active ? 'Channel partner activated' : 'Channel partner blocked');
    } catch (err) {
      showToast('error', err instanceof Error ? err.message : 'Failed to update status');
    } finally {
      setTogglingId(null);
    }
  };

  const confirmRevoke = async () => {
    if (!revokeTarget) return;
    setRevoking(true);
    try {
      await adminChannelPartnerService.revoke(revokeTarget.id);
      setPartners((prev) => prev.filter((p) => p.id !== revokeTarget.id));
      showToast('success', `Channel partner access revoked for "${revokeTarget.company_name}"`);
      setRevokeTarget(null);
    } catch (err) {
      showToast('error', err instanceof Error ? err.message : 'Failed to revoke access');
    } finally {
      setRevoking(false);
    }
  };

  return (
    <div className="manage-cp">
      {toast && (
        <div className={`admin-cp-toast ${toast.type}`} style={{ marginBottom: '1.25rem' }}>
          {toast.text}
        </div>
      )}

      <div className="admin-cp-header">
        <div>
          <h1 className="admin-cp-title">
            <span className="admin-cp-title-icon">
              <Building2 />
            </span>
            Channel Partners
          </h1>
          <p>
            Channel partners log in with their phone number on the normal login page and manage
            auctions from the same dashboard as any user.
          </p>
        </div>
        <button type="button" onClick={openCreateModal} className="admin-cp-add-btn">
          <Plus className="w-4 h-4" />
          Add Channel Partner
        </button>
      </div>

      <div className="admin-cp-card">
        {loading ? (
          <div className="admin-cp-state">
            <RefreshCw className="w-5 h-5 animate-spin" />
            Loading channel partners...
          </div>
        ) : error ? (
          <div className="admin-cp-error">
            <span>{error}</span>
            <button type="button" onClick={loadPartners}>
              Retry
            </button>
          </div>
        ) : partners.length === 0 ? (
          <div className="admin-cp-state">
            No channel partners yet. Add one to grant a company its own auction management panel.
          </div>
        ) : (
          <div className="admin-cp-table-wrap">
            <table className="admin-cp-table">
              <thead>
                <tr>
                  <th>Company</th>
                  <th>Contact</th>
                  <th>Phone</th>
                  <th>Auctions</th>
                  <th>Status</th>
                  <th style={{ textAlign: 'right' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {partners.map((partner) => (
                  <tr key={partner.id}>
                    <td>
                      <div className="admin-cp-company-name">{partner.company_name}</div>
                      {partner.company_product_service && (
                        <div className="admin-cp-company-sub">{partner.company_product_service}</div>
                      )}
                    </td>
                    <td>
                      <div className="admin-cp-person-name">{partner.person_name}</div>
                      {partner.email && <div className="admin-cp-company-sub">{partner.email}</div>}
                    </td>
                    <td style={{ whiteSpace: 'nowrap' }}>+91 {partner.phone_number}</td>
                    <td>
                      <span className="admin-cp-auctions-count">
                        <Gavel className="w-3.5 h-3.5" />
                        {partner.auctions_created}
                      </span>
                    </td>
                    <td>
                      <button
                        type="button"
                        onClick={() => toggleActive(partner)}
                        disabled={togglingId === partner.id}
                        className={`admin-cp-status-toggle ${partner.is_active ? 'active' : 'blocked'}`}
                      >
                        {togglingId === partner.id ? (
                          <RefreshCw className="w-3 h-3 animate-spin" />
                        ) : partner.is_active ? (
                          <CheckCircle2 className="w-3 h-3" />
                        ) : (
                          <Ban className="w-3 h-3" />
                        )}
                        {partner.is_active ? 'Active' : 'Blocked'}
                      </button>
                    </td>
                    <td>
                      <div className="admin-cp-row-actions">
                        <button
                          type="button"
                          onClick={() => openEditModal(partner)}
                          className="admin-cp-icon-btn edit"
                          aria-label={`Edit ${partner.company_name}`}
                        >
                          <Pencil className="w-4 h-4" />
                        </button>
                        <button
                          type="button"
                          onClick={() => setRevokeTarget(partner)}
                          className="admin-cp-icon-btn revoke"
                          aria-label={`Revoke ${partner.company_name}`}
                        >
                          <ShieldOff className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Create / Edit modal */}
      {isModalOpen && (
        <div className="admin-cp-modal-backdrop">
          <div className="admin-cp-modal">
            <div className="admin-cp-modal-header">
              <h3>{editingId ? 'Edit Channel Partner' : 'Add Channel Partner'}</h3>
              <button type="button" onClick={closeModal} className="admin-cp-modal-close" aria-label="Close">
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="admin-cp-form">
              <div className="admin-cp-field">
                <label>
                  Company Name<span className="required">*</span>
                </label>
                <input
                  type="text"
                  value={form.company_name}
                  onChange={(e) => setForm({ ...form, company_name: e.target.value })}
                  placeholder="e.g. Sharma Logistics Pvt Ltd"
                  className={formErrors.company_name ? 'error' : ''}
                />
                {formErrors.company_name && <p className="admin-cp-field-error">{formErrors.company_name}</p>}
              </div>

              <div className="admin-cp-field-row">
                <div className="admin-cp-field">
                  <label>
                    Contact Person<span className="required">*</span>
                  </label>
                  <input
                    type="text"
                    value={form.person_name}
                    onChange={(e) => setForm({ ...form, person_name: e.target.value })}
                    placeholder="e.g. Rakesh Sharma"
                    className={formErrors.person_name ? 'error' : ''}
                  />
                  {formErrors.person_name && <p className="admin-cp-field-error">{formErrors.person_name}</p>}
                </div>
                <div className="admin-cp-field">
                  <label>
                    Phone Number<span className="required">*</span>
                  </label>
                  <input
                    type="text"
                    value={form.phone_number}
                    onChange={(e) => setForm({ ...form, phone_number: e.target.value })}
                    placeholder="10-digit number"
                    disabled={!!editingId}
                    className={formErrors.phone_number ? 'error' : ''}
                  />
                  {formErrors.phone_number && <p className="admin-cp-field-error">{formErrors.phone_number}</p>}
                  {editingId && <p className="admin-cp-field-hint">Phone number can't be changed after creation.</p>}
                </div>
              </div>

              <div className="admin-cp-field">
                <label>Email</label>
                <input
                  type="email"
                  value={form.email || ''}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  placeholder="contact@company.com"
                  className={formErrors.email ? 'error' : ''}
                />
                {formErrors.email && <p className="admin-cp-field-error">{formErrors.email}</p>}
              </div>

              <div className="admin-cp-field">
                <label>Company Address</label>
                <textarea
                  rows={2}
                  value={form.company_address || ''}
                  onChange={(e) => setForm({ ...form, company_address: e.target.value })}
                  placeholder="Registered office address"
                />
              </div>

              <div className="admin-cp-field">
                <label>Products / Services</label>
                <input
                  type="text"
                  value={form.company_product_service || ''}
                  onChange={(e) => setForm({ ...form, company_product_service: e.target.value })}
                  placeholder="e.g. Industrial equipment resale"
                />
              </div>

              {!editingId && (
                <p className="admin-cp-form-note">
                  No password is set here. The partner logs in with an OTP sent to this phone number,
                  exactly like any other user.
                </p>
              )}

              <div className="admin-cp-form-actions">
                <button
                  type="button"
                  onClick={closeModal}
                  disabled={saving}
                  className="admin-cp-btn-secondary"
                >
                  Cancel
                </button>
                <button type="submit" disabled={saving} className="admin-cp-btn-primary">
                  {saving ? (
                    <>
                      <RefreshCw className="w-4 h-4 animate-spin" />
                      Saving
                    </>
                  ) : (
                    <>
                      <Save className="w-4 h-4" />
                      {editingId ? 'Save Changes' : 'Create Channel Partner'}
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Revoke confirmation */}
      {revokeTarget && (
        <div className="admin-cp-modal-backdrop">
          <div className="admin-cp-confirm">
            <div className="admin-cp-confirm-body">
              <div className="admin-cp-confirm-icon">
                <AlertTriangle className="w-5 h-5" />
              </div>
              <div>
                <h3>Revoke channel partner access?</h3>
                <p>
                  "{revokeTarget.company_name}" will no longer be a channel partner. Their account
                  becomes a regular user and keeps its auction history &mdash; nothing is deleted.
                </p>
              </div>
            </div>
            <div className="admin-cp-confirm-actions">
              <button
                type="button"
                onClick={() => setRevokeTarget(null)}
                disabled={revoking}
                className="admin-cp-btn-secondary"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={confirmRevoke}
                disabled={revoking}
                className="admin-cp-btn-danger"
              >
                {revoking ? <RefreshCw className="w-4 h-4 animate-spin" /> : <ShieldOff className="w-4 h-4" />}
                Revoke Access
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ManageChannelPartners;
